
import type { ReactNode } from "react";
import type { components } from "@/api/schema";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/status-pill";
import { shortDate } from "@/lib/delivery-timing";
import type { StatusTone } from "@/lib/status-tones";
import { cn, parseUTCDate } from "@/lib/utils";

export type PurchaseOrderTimeline =
  components["schemas"]["PurchaseOrderTimelineRead"];

const STATUS: Record<
  PurchaseOrderTimeline["status"],
  { label: string; tone: StatusTone }
> = {
  on_track: { label: "On track", tone: "success" },
  at_risk: { label: "At risk", tone: "warning" },
  late: { label: "Late", tone: "danger" },
  delivered: { label: "Delivered", tone: "neutral" },
};

export function TimelineStatus({
  status,
}: {
  status: PurchaseOrderTimeline["status"];
}) {
  const meta = STATUS[status];
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
}

function day(value?: string | null) {
  return value ? shortDate(value) : "—";
}

/** Today's estimate; a lead-time estimate (not yet from the manufacturer or carrier) reads "est.". */
function Expected({
  value,
  firm,
}: {
  value?: string | null;
  firm: boolean;
}) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="whitespace-nowrap tabular-nums">
      {shortDate(value)}
      {firm ? null : (
        <span className="ml-1 text-xs font-normal text-muted-foreground">
          est.
        </span>
      )}
    </span>
  );
}

/** The business day after an ISO date: the PO's expected send day while it hasn't gone out (our processing day). */
function nextBusinessDay(iso: string): string {
  const date = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  do date.setUTCDate(date.getUTCDate() + 1);
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6);
  return date.toISOString().slice(0, 10);
}

/** When the PO was ordered and sent, then its planned ship and arrival next to today's, against the deliver-by date. */
export function DeliveryTimelineCard({
  timeline,
  chart,
}: {
  timeline: PurchaseOrderTimeline;
  /** The PO's bar from the project's expected timeline, drawn above the planned/now dates. */
  chart?: ReactNode;
}) {
  const delivered = timeline.status === "delivered";
  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Delivery timeline
          </h3>
          <TimelineStatus status={timeline.status} />
        </div>
        {timeline.deliver_by ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-xs text-muted-foreground">Deliver by</dt>
            <dt className="text-xs text-muted-foreground">Ship by</dt>
            <dd className="font-medium tabular-nums">
              {day(timeline.deliver_by)}
            </dd>
            <dd className="font-medium tabular-nums">
              {day(timeline.ship_by)}
            </dd>
          </dl>
        ) : null}
        {chart}
        {/* Planned next to now, as a grid (not a data table: the PO page's table is its line items). */}
        <div
          className={cn(
            "grid items-baseline gap-x-3 gap-y-1 text-sm",
            delivered ? "grid-cols-[auto_1fr]" : "grid-cols-[auto_1fr_1fr]",
          )}
        >
          <span />
          <span className="text-xs text-muted-foreground">Planned</span>
          {delivered ? null : <span className="text-xs text-muted-foreground">Now</span>}
          {/* Planned is the day we ordered; now is when the PO went out, or the next business day until it does. */}
          <span className="text-xs text-muted-foreground">Ordered</span>
          <span className="tabular-nums">{day(timeline.ordered_on)}</span>
          {delivered ? null : (
            <span className="font-medium">
              {timeline.sent_on ? (
                <Expected value={timeline.sent_on} firm />
              ) : (
                <Expected value={nextBusinessDay(timeline.ordered_on)} firm={false} />
              )}
            </span>
          )}
          <span className="text-xs text-muted-foreground">Ships</span>
          <span className="tabular-nums">{day(timeline.planned_ship_on)}</span>
          {delivered ? null : (
            <span className="font-medium">
              {timeline.shipped ? (
                "Shipped"
              ) : (
                <Expected value={timeline.expected_ship_on} firm={timeline.firm} />
              )}
            </span>
          )}
          <span className="text-xs text-muted-foreground">Arrives</span>
          <span className="tabular-nums">{day(timeline.planned_arrival_on)}</span>
          {delivered ? null : (
            <span className="font-medium">
              <Expected value={timeline.expected_arrival_on} firm={timeline.firm} />
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * One row under a PO on the project, in the PO's shared pill column (a ``col-span-2`` subgrid row):
 * the status, today's ship and arrival, and the planned arrival when it differs from today's.
 * On a phone the pill and dates flow as one wrapping line.
 */
export function POTimelineLine({ timeline }: { timeline: PurchaseOrderTimeline }) {
  if (timeline.status === "delivered") return null;
  const planned = timeline.planned_arrival_on;
  const showPlanned =
    planned != null &&
    planned.slice(0, 10) !== timeline.expected_arrival_on?.slice(0, 10);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:col-span-2 sm:grid sm:grid-cols-subgrid sm:justify-items-start">
      <TimelineStatus status={timeline.status} />
      <span className="contents sm:flex sm:min-w-0 sm:w-full sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0.5">
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">Ships </span>
          {timeline.shipped ? (
            "shipped"
          ) : (
            <Expected value={timeline.expected_ship_on} firm={timeline.firm} />
          )}
        </span>
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">Arrives </span>
          <Expected value={timeline.expected_arrival_on} firm={timeline.firm} />
        </span>
        {showPlanned ? (
          <span className="whitespace-nowrap text-muted-foreground">Planned {day(planned)}</span>
        ) : null}
      </span>
    </span>
  );
}

/** When the project was started (the customer ordered), on the local calendar: "Started Mon, Nov 2" (USA-1718). */
export function StartedDate({ startedAt }: { startedAt: string }) {
  const date = parseUTCDate(startedAt);
  const label = date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
  return (
    <span className="whitespace-nowrap">
      <span className="text-muted-foreground">Started </span>
      <span className="font-medium tabular-nums">{label}</span>
    </span>
  );
}

const RANK: Record<PurchaseOrderTimeline["status"], number> = {
  delivered: 0,
  on_track: 1,
  at_risk: 2,
  late: 3,
};

/** The project's timeline from its POs: the deliver-by date, when everything ships and when it all arrives. */
export function ProjectDeliveryDates({
  timelines,
  deliverByEditor,
  className,
}: {
  timelines: PurchaseOrderTimeline[];
  /** Shown in place of the deliver-by date, even with nothing on its way: the project's date picker (USA-1475). */
  deliverByEditor?: ReactNode;
  className?: string;
}) {
  const open = timelines.filter((timeline) => timeline.status !== "delivered");
  if (!open.length) {
    return deliverByEditor ? (
      <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-sm", className)}>
        {deliverByEditor}
      </div>
    ) : null;
  }
  const latest = (values: (string | null | undefined)[]) =>
    values.filter((value): value is string => Boolean(value)).sort().at(-1);
  const deliverBy = latest(timelines.map((timeline) => timeline.deliver_by));
  const unshipped = open.filter((timeline) => !timeline.shipped);
  const shipBy = latest(unshipped.map((timeline) => timeline.expected_ship_on));
  const arriveBy = latest(open.map((timeline) => timeline.expected_arrival_on));
  const worst = open.reduce((a, b) => (RANK[b.status] > RANK[a.status] ? b : a));
  const firmShips = unshipped.every((timeline) => timeline.firm);
  const firmArrivals = open.every((timeline) => timeline.firm);
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-1 text-sm",
        className,
      )}
    >
      <TimelineStatus status={worst.status} />
      {deliverByEditor ?? (deliverBy ? (
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">Deliver by </span>
          <span className="font-medium">{shortDate(deliverBy)}</span>
        </span>
      ) : null)}
      {shipBy ? (
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">All shipped by </span>
          <span className="font-medium">
            <Expected value={shipBy} firm={firmShips} />
          </span>
        </span>
      ) : null}
      {arriveBy ? (
        <span className="whitespace-nowrap">
          <span className="text-muted-foreground">All delivered by </span>
          <span className="font-medium">
            <Expected value={arriveBy} firm={firmArrivals} />
          </span>
        </span>
      ) : null}
    </div>
  );
}
